/**
 * WritingEditorSimple Component
 *
 * Simple textarea-based editor for essay writing with word count and timer.
 * Replaces React-Quill to avoid React 19 compatibility issues.
 */

import React, { useState, useEffect, useRef } from 'react';
import './WritingEditor.css';

// localStorage key for persisted drafts (per prompt)
const draftStorageKey = (draftKey) => `selective-writing-draft-${draftKey || 'default'}`;

const WritingEditorSimple = ({
  initialValue = '',
  minWords = 250,
  maxWords = 600,
  timeLimit = null, // in seconds, null for no limit
  onTextChange,
  onSubmit,
  disabled = false,
  autoSave = true,
  autoSaveInterval = 5000, // 5 seconds
  draftKey = null // usually the prompt id; enables draft persistence
}) => {
  // Restore a persisted draft (if any) so students don't lose work
  const [text, setText] = useState(() => {
    if (initialValue) return initialValue;
    if (autoSave) {
      try {
        return localStorage.getItem(draftStorageKey(draftKey)) || '';
      } catch (e) {
        return '';
      }
    }
    return '';
  });
  const [wordCount, setWordCount] = useState(0);
  const [characterCount, setCharacterCount] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(timeLimit);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved'
  const [showWarning, setShowWarning] = useState(false);

  const textareaRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  // Calculate word count
  const countWords = (content) => {
    if (!content || content.trim().length === 0) return 0;
    return content.trim().split(/\s+/).length;
  };

  // Handle text changes
  useEffect(() => {
    const words = countWords(text);
    const chars = text.length;

    setWordCount(words);
    setCharacterCount(chars);

    if (onTextChange) {
      onTextChange(text);
    }

    // Mark as unsaved
    if (text !== initialValue) {
      setSaveStatus('unsaved');
    }
  }, [text, initialValue, onTextChange]);

  // Auto-save: persist the draft to localStorage so it survives navigation/reload
  useEffect(() => {
    if (!autoSave || text === initialValue) return;

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer
    autoSaveTimerRef.current = setTimeout(() => {
      setSaveStatus('saving');
      try {
        localStorage.setItem(draftStorageKey(draftKey), text);
        setSaveStatus('saved');
      } catch (e) {
        console.error('Failed to save draft:', e);
        setSaveStatus('unsaved');
      }
    }, autoSaveInterval);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [text, autoSave, autoSaveInterval, initialValue, draftKey]);

  // Remove the persisted draft once the essay is submitted
  const clearDraft = () => {
    try {
      localStorage.removeItem(draftStorageKey(draftKey));
    } catch (e) {
      // non-fatal
    }
  };

  // Countdown timer
  useEffect(() => {
    if (!timeLimit || timeRemaining === null) return;

    // Show warning at 5 minutes
    if (timeRemaining <= 300 && timeRemaining > 0) {
      setShowWarning(true);
    }

    // Start countdown
    countdownTimerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
    // eslint-disable-next-line
  }, [timeLimit]);

  const handleAutoSubmit = () => {
    if (onSubmit && text.trim().length > 0) {
      alert('Time is up! Your essay will be submitted automatically.');
      clearDraft();
      onSubmit(text);
    }
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  const handleSubmit = () => {
    if (wordCount < minWords) {
      alert(`Your essay must be at least ${minWords} words. Current count: ${wordCount}`);
      return;
    }
    if (wordCount > maxWords) {
      alert(`Your essay must not exceed ${maxWords} words. Current count: ${wordCount}`);
      return;
    }
    if (onSubmit) {
      clearDraft();
      onSubmit(text);
    }
  };

  // Format time remaining
  const formatTime = (seconds) => {
    if (seconds === null) return 'No time limit';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Get word count color
  const getWordCountColor = () => {
    if (wordCount < minWords) return '#ef4444'; // red
    if (wordCount > maxWords) return '#ef4444'; // red
    if (wordCount >= minWords && wordCount <= maxWords) return '#10b981'; // green
    return '#94a3b8'; // gray
  };

  // Get progress percentage
  const getProgress = () => {
    if (wordCount < minWords) {
      return (wordCount / minWords) * 100;
    }
    return 100;
  };

  return (
    <div className="writing-editor">
      {/* Editor Toolbar */}
      <div className="editor-toolbar">
        <div className="editor-stats">
          <div className="stat-item">
            <span className="stat-label">Words:</span>
            <span className="stat-value" style={{ color: getWordCountColor() }}>
              {wordCount} / {minWords}-{maxWords}
            </span>
          </div>

          <div className="stat-item">
            <span className="stat-label">Characters:</span>
            <span className="stat-value">{characterCount}</span>
          </div>

          {timeLimit && (
            <div className={`stat-item ${showWarning ? 'warning' : ''}`}>
              <span className="stat-label">Time:</span>
              <span className="stat-value">{formatTime(timeRemaining)}</span>
            </div>
          )}

          <div className="stat-item">
            <span className={`save-indicator save-${saveStatus}`}>
              {saveStatus === 'saved' && '✓ Saved'}
              {saveStatus === 'saving' && '⟳ Saving...'}
              {saveStatus === 'unsaved' && '○ Unsaved'}
            </span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="word-count-progress">
        <div
          className="progress-bar"
          style={{
            width: `${Math.min(getProgress(), 100)}%`,
            backgroundColor: getWordCountColor()
          }}
        />
      </div>

      {/* Text Area */}
      <div className="editor-container">
        <textarea
          ref={textareaRef}
          className="editor-textarea"
          value={text}
          onChange={handleTextChange}
          disabled={disabled}
          placeholder="Start writing your essay here..."
          spellCheck={true}
        />
      </div>

      {/* Submit Button */}
      <div className="editor-actions">
        <button
          className="submit-button"
          onClick={handleSubmit}
          disabled={disabled || wordCount < minWords || wordCount > maxWords}
        >
          Submit Essay for Assessment
        </button>

        {wordCount < minWords && (
          <p className="word-count-hint">
            Write at least {minWords - wordCount} more words to submit
          </p>
        )}

        {wordCount > maxWords && (
          <p className="word-count-hint warning">
            Remove {wordCount - maxWords} words to submit
          </p>
        )}
      </div>
    </div>
  );
};

export default WritingEditorSimple;
